import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { timeAgo } from '../../data/mockData';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { PushToggle } from './PushToggle';

const PRIORITY_DOT = {
  CRITICAL: 'bg-kelate-red',
  HIGH: 'bg-kelate-wood',
  NORMAL: 'bg-kelate-sage',
  LOW: 'bg-kelate-line',
};

export function NotificationBell({ recipientType, recipientId = null, fullPageUrl }) {
  const { notifications, unreadCount, markRead, markAllRead } = useApp();
  const navigate = useNavigate();
  const items = notifications.filter((n) => n.recipientType === recipientType && (!recipientId || !n.recipientId || n.recipientId === recipientId)).slice(0, 12);
  const unread = unreadCount(recipientType, recipientId);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid={`notification-bell-${recipientType}`} className="relative rounded-sm p-2 text-kelate-ink hover:bg-secondary" aria-label="Notifications">
          <Bell size={18} />
          {unread > 0 && (
            <span data-testid={`notification-badge-${recipientType}`} className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-kelate-red px-1 text-[10px] font-semibold text-white">
              {unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 max-w-[92vw] p-0 bg-kelate-surface border-kelate-line">
        <div className="flex items-center justify-between border-b border-kelate-line px-4 py-3">
          <span className="text-sm font-semibold text-kelate-ink">Notifications</span>
          <span className="flex items-center gap-3">
            <PushToggle />
            <button data-testid="mark-all-read" onClick={() => markAllRead(recipientType, recipientId)} className="flex items-center gap-1 text-xs text-kelate-wood hover:text-kelate-darkwood">
              <CheckCheck size={12} /> Mark all read
            </button>
          </span>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <div className="px-4 py-8 text-center text-sm text-kelate-soft">No notifications yet</div>}
          {items.map((n) => (
            <button
              key={n.id}
              data-testid={`notification-item-${n.id}`}
              onClick={() => { markRead(n.id); if (n.actionUrl) navigate(n.actionUrl); }}
              className={`flex w-full items-start gap-3 border-b border-kelate-line/60 px-4 py-3 text-left transition-colors hover:bg-secondary/60 ${n.isRead ? 'opacity-60' : ''}`}
            >
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${PRIORITY_DOT[n.priority] || 'bg-kelate-line'}`} />
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-kelate-ink">{n.title}</span>
                <span className="mt-0.5 block text-xs text-kelate-soft leading-snug">{n.message}</span>
                <span className="mt-1 block text-[10px] uppercase tracking-wide text-kelate-soft">{n.category} · {timeAgo(n.createdAt)}</span>
              </span>
            </button>
          ))}
        </div>
        {fullPageUrl && (
          <button onClick={() => navigate(fullPageUrl)} data-testid="view-all-notifications" className="w-full border-t border-kelate-line px-4 py-2.5 text-center text-xs font-medium text-kelate-wood hover:bg-secondary">
            View all
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
